<template>
  <v-container fluid>
    <v-row dense>
      <!-- ERRORS -->
      <v-col v-if="errors.length" cols="12">
        <v-alert
          v-for="(item, index) in errors"
          :key="index"
          dense
          type="error"
          outlined
          dismissible
          class="mb-2"
        >
          {{ item[0] }}
        </v-alert>
      </v-col>

      <!-- STOCK SELECT -->
      <v-col cols="12">
        <v-autocomplete
          v-model="stockId"
          :items="splrPrdList"
          item-value="stock_id"
          label="Stock Code"
          :loading="splrPrdLoading"
          :search-input.sync="searchInput"
          no-filter
          clearable
          outlined
          dense
          hide-details="auto"
        >
          <template v-slot:selection="{ item }">
            <span class="font-weight-medium">
              {{ item.stock_code }}
            </span>
            <span class="grey--text text-caption ml-2">
              {{ item.product_name }} ({{ item.brand_name }})
            </span>
          </template>

          <template v-slot:item="{ item }">
            <div>
              <div class="primary--text font-weight-medium">
                {{ item.stock_code }}
              </div>
              <div class="text-caption grey--text">
                {{ item.product_name }} ({{ item.brand_name }})
              </div>
            </div>
          </template>
        </v-autocomplete>
      </v-col>

      <!-- PRICE -->
      <v-col cols="12">
        <v-text-field
          v-model.number="retail"
          label="Price"
          type="number"
          :disabled="!stockId"
          outlined
          dense
          hide-details="auto"
          prefix="₱"
        />
      </v-col>

      <!-- QUANTITY -->
      <v-col cols="12">
        <v-text-field
          v-model.number="quantity"
          label="Quantity"
          type="number"
          :disabled="!stockId"
          :suffix="unit ? `/ ${unit}` : ''"
          outlined
          dense
          hide-details="auto"
        />
      </v-col>

      <!-- DISCOUNT (same peso/percent entry as Cash Sales) -->
      <v-col cols="12">
        <div class="d-flex align-center" style="gap: 6px">
          <v-text-field
            v-if="discountMode === 'percent'"
            v-model.number="itemDiscountPercentage"
            class="flex-grow-1"
            label="Discount/Item"
            suffix="%"
            type="number"
            min="0"
            max="100"
            :disabled="!stockId"
            outlined
            dense
            hide-details="auto"
          />
          <v-text-field
            v-else
            v-model.number="itemDiscountFixed"
            class="flex-grow-1"
            label="Discount/Item"
            prefix="₱"
            type="number"
            min="0"
            :disabled="!stockId"
            outlined
            dense
            hide-details="auto"
          />
          <v-btn-toggle v-model="discountMode" mandatory dense>
            <v-btn small value="fixed">
              ₱
            </v-btn>
            <v-btn small value="percent">
              %
            </v-btn>
          </v-btn-toggle>
        </div>
      </v-col>

      <!-- TOTAL DISCOUNT -->
      <v-col cols="12">
        <v-text-field
          v-model.number="discount"
          label="Total Discounted Amnt"
          prefix="₱"
          type="number"
          :disabled="!stockId"
          outlined
          dense
          hide-details="auto"
        />
      </v-col>

      <!-- SAVE BUTTON -->
      <v-col cols="12" class="mt-2">
        <v-btn
          block
          color="primary"
          dark
          :loading="updating"
          :disabled="!stockId || !quantity || !retail"
          @click="update"
        >
          <v-icon left>
            mdi-content-save
          </v-icon>
          Save Changes
        </v-btn>
      </v-col>
    </v-row>
  </v-container>
</template>
<script>
import { mapState } from 'vuex'
import searchDelay from '@/extra-functions/searchDelay.extra'
import splrPrdRepository from '@/repositories/supplier-product.repository'
import { errorHandler } from '@/extra-functions/store.extra'
import consumerLoanInventoryRepository from '@/repositories/consumer-loan-inventory.repository'
import { apiVars } from '@/extra-functions/api-helper.extra'
import { roundOff } from '@/lib/utils'
const splrPrdDelay = searchDelay()
export default {
  name: 'EditConsumerLoanInventory',
  props: {
    propConsumerId: String,
    propConsumerLoanId: String,
    propConsumerLoanInventory: Object,
  },
  data() {
    return {
      ...apiVars('splrPrd'),
      stockId: null,
      retail: null,
      unit: 'none',
      quantity: null,
      updating: false,
      errors: [],
      searchInput: null,
      // Stock the form opened on — lets selectedStock tell "the list finally
      // resolved the item I am editing" apart from "the user picked a stock"
      hydratedStockId: null,
      // Discount entry mirrors Cash Sales: the per-item figure can be typed as
      // pesos or a percent of the price, and `discount` is the line's total
      discountMode: 'fixed',
      itemDiscountPercentage: null,
      itemDiscountFixed: null,
      discount: null,
      // Set while loading a line into the form so the watchers below do not
      // rewrite the saved total from the rounded per-item figure
      hydratingDiscount: false,
    }
  },
  created() {
    this.getSplrPrd()
  },
  computed: {
    ...mapState({
      storeId: (state) => state.settings.storeId,
      supplierProductUpdate: (state) => state.supplierProduct.channelUpdate,
    }),
    selectedStock() {
      return this.splrPrdList.find((e) => e.stock_id === this.stockId)
    },
    // Derived from the chosen stock like Create does; falls back to the item's
    // own supplier while the list is still loading
    supplierId() {
      return (
        this.selectedStock?.supplier_id ??
        this.propConsumerLoanInventory?.supplier_id ??
        null
      )
    },
  },
  watch: {
    propConsumerLoanInventory: {
      immediate: true,
      handler(data) {
        if (!data || !data.stock_id) return
        this.hydratingDiscount = true
        this.$nextTick(() => {
          this.hydratingDiscount = false
        })
        this.hydratedStockId = data.stock_id
        this.stockId = data.stock_id
        this.retail = data.retail
        this.unit = data.unit
        this.quantity = data.quantity
        // Back out the per-item figures from the saved line total so both
        // entry modes open showing the discount already on the item
        this.discount = data.discount ?? null
        this.hydrateItemDiscount()
        // Seed the search so the item's own stock is in the fetched page and
        // the autocomplete can render the selection
        this.searchInput = data.stock_code ?? null
      },
    },
    searchInput(val) {
      if (!val && val !== undefined) return
      this.getSplrPrd()
    },
    selectedStock(obj) {
      if (!obj) return
      // Do not overwrite the saved price/unit with the stock's current
      // defaults just because the list resolved the item being edited
      if (this.hydratedStockId === obj.stock_id) {
        this.hydratedStockId = null
        return
      }
      this.retail = obj.retail
      this.unit = obj.unit
    },
    // Watchers rather than @keyup so the total also follows the number
    // spinners, paste, and a price change from picking a different stock
    quantity() {
      this.recalculateDiscount()
    },
    retail() {
      this.recalculateDiscount()
    },
    itemDiscountFixed() {
      if (this.discountMode !== 'fixed') return
      this.onItemDiscountFixedChange()
    },
    itemDiscountPercentage() {
      if (this.discountMode !== 'percent') return
      this.onItemDiscountPercentageChange()
    },
    discountMode() {
      this.recalculateDiscount()
    },
    supplierProductUpdate() {
      this.getSplrPrd()
    },
  },
  methods: {
    // Percent and peso stay in step with each other and with the line total,
    // exactly as they do on the Cash Sales form
    // Single source of truth: these read the model values only. Reading
    // e.target.value from @keyup as well meant two paths wrote `discount`,
    // and a type="number" input reports "" for a half-typed decimal.
    onItemDiscountPercentageChange() {
      if (this.hydratingDiscount) return
      const percent = Number(this.itemDiscountPercentage) || 0
      const retail = Number(this.retail) || 0
      const quantity = Number(this.quantity) || 0
      this.discount = roundOff((percent / 100) * retail * quantity)
      this.itemDiscountFixed = roundOff((percent / 100) * retail)
    },
    onItemDiscountFixedChange() {
      if (this.hydratingDiscount) return
      const perItem = Number(this.itemDiscountFixed) || 0
      const retail = Number(this.retail) || 0
      const quantity = Number(this.quantity) || 0
      this.discount = roundOff(perItem * quantity)
      this.itemDiscountPercentage = retail
        ? Math.round((perItem / retail) * 1000) / 10
        : null
    },
    // Price or quantity moved, so the line total has to follow
    recalculateDiscount() {
      if (this.hydratingDiscount) return
      if (this.discountMode === 'fixed') {
        this.onItemDiscountFixedChange()
      } else {
        this.onItemDiscountPercentageChange()
      }
    },
    // Saved lines store only the total, so derive the per-item figures for
    // display without touching the stored value
    hydrateItemDiscount() {
      const total = Number(this.discount) || 0
      const quantity = Number(this.quantity) || 0
      const retail = Number(this.retail) || 0
      const perItem = quantity ? roundOff(total / quantity) : 0
      this.itemDiscountFixed = perItem || null
      this.itemDiscountPercentage = retail
        ? Math.round((perItem / retail) * 1000) / 10
        : null
    },
    getSplrPrd() {
      this.splrPrdLoading = true
      this.splrPrdErrors = []
      splrPrdDelay(() => {
        splrPrdRepository
          .paginate(this.storeId, { search: this.searchInput })
          .then(({ data }) => {
            this.splrPrdList = data
          })
          .catch((e) => {
            this.splrPrdErrors = errorHandler(e)
          })
          .then(() => {
            this.splrPrdLoading = false
          })
      })
    },
    update() {
      if (!this.updating) {
        this.updating = true
        this.errors = []

        const data = {
          consumer_loan_id: this.propConsumerLoanId,
          supplier_id: this.supplierId,
          stock_id: this.stockId,
          unit: this.unit,
          retail: this.retail,
          quantity: this.quantity,
          discount: this.discount || 0,
        }
        const id = this.propConsumerLoanInventory.id
        const params = {
          consumer: this.propConsumerId,
          consumerLoan: this.propConsumerLoanId,
        }
        consumerLoanInventoryRepository
          .update(params, { id, data })
          .then(() => {
            this.$emit('close-dialog')
          })
          .catch((e) => {
            this.errors = errorHandler(e)
          })
          .then(() => {
            this.updating = false
          })
      }
    },
  },
}
</script>
